import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../apiConfig";
import "../Styles/ManageLeaveTypes.css";
import { Calendar, Plus, AlertCircle, Loader, Trash2, Edit, Check, X } from "lucide-react";
import Toast from "../Components/Common/Toast"; 
const ManageLeaveTypes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { companyId, companyName } = location.state || {};

  const [leaveTypes, setLeaveTypes] = useState([]);
  const [newLeaveType, setNewLeaveType] = useState({ id: "", name: "", maxDays: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [leaveTypeToDelete, setLeaveTypeToDelete] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('idToken');
    if (!token) {
      navigate('/');
      return;
    }
    if (companyId) {
      fetchLeaveTypes(companyId);
    } else {
      setLoading(false);
      setError("No company selected. Please select a company first.");
    }
  }, [companyId, navigate]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 5000);
  };

  const fetchLeaveTypes = async (companyId) => {
    try {
      setLoading(true);
      setError("");
      
      const response = await fetch(`${API_BASE_URL}/leaves/types/${companyId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
      });
      if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          window.location.href = '/';
          return;
        }
      const data = await response.json();
      if (data.success) {
        
        const typesWithDocId = data.leaveTypes.map(type => ({
          ...type,
          documentId: type.documentId || type.id
        }));
        setLeaveTypes(typesWithDocId || []);
      } else {
        setError("Failed to fetch leave types.");
        setLeaveTypes([]);
      }
    } catch (err) {
      console.error("Error fetching leave types:", err);
      setError("An error occurred while fetching leave types.");
      setLeaveTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLeaveType = async (e) => {
    e.preventDefault();
    
    if (!companyId) {
      setError("No company selected.");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      
      const response = await fetch(`${API_BASE_URL}/leaves/types`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
        body: JSON.stringify({ companyId, leaveType: newLeaveType }),
      });
  
      const data = await response.json();
      if (data.success) {
        setSuccess("Leave type added successfully!");
        fetchLeaveTypes(companyId);
        setNewLeaveType({ id: "", name: "", maxDays: "" });
      } else {
        setError(data.message || "Failed to add leave type.");
      }
    } catch (err) {
      console.error("Error adding leave type:", err);
      setError("An error occurred while adding the leave type.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditLeaveType = (leaveType) => {
    setEditingLeaveType(leaveType);
    setNewLeaveType({
      id: leaveType.id,
      name: leaveType.name,
      maxDays: leaveType.maxDays
    });
    setShowEditDialog(true);
  };

  const handleUpdateLeaveType = async (e) => {
    e.preventDefault();
    
    if (!companyId || !editingLeaveType) {
      setError("No company or leave type selected.");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      
     
      const docId = editingLeaveType.documentId || editingLeaveType.id;
      
      const response = await fetch(`${API_BASE_URL}/leaves/types/${companyId}/${docId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
        body: JSON.stringify({ leaveType: newLeaveType }),
      });
  
      const data = await response.json();
      if (data.success) {
        showNotification('success', "Leave type updated successfully!");
        fetchLeaveTypes(companyId);
        setNewLeaveType({ id: "", name: "", maxDays: "" });
        setShowEditDialog(false);
        setEditingLeaveType(null);
      } else {
        showNotification('error', data.message || "Failed to update leave type.");
      }
    } catch (err) {
      console.error("Error updating leave type:", err);
      showNotification('error', "An error occurred while updating the leave type.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLeaveType = (leaveTypeId, documentId) => {
    // Store the leave type info for deletion
    setLeaveTypeToDelete({
      id: leaveTypeId,
      documentId: documentId
    });
    setShowDeleteConfirm(true);
  };

  const confirmDeleteLeaveType = async () => {
    try {
      setLoading(true);
      
      // Use documentId if available, otherwise use the id
      const idToUse = leaveTypeToDelete.documentId || leaveTypeToDelete.id;
      
      const response = await fetch(`${API_BASE_URL}/leaves/types/${companyId}/${idToUse}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
      });
  
      const data = await response.json();
      if (data.success) {
        showNotification('success', "Leave type deleted successfully!");
        fetchLeaveTypes(companyId);
      } else {
        showNotification('error', data.message || "Failed to delete leave type.");
      }
    } catch (err) {
      console.error("Error deleting leave type:", err);
      showNotification('error', "An error occurred while deleting the leave type.");
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setLeaveTypeToDelete(null);
    }
  };

  const cancelDeleteLeaveType = () => {
    setShowDeleteConfirm(false);
    setLeaveTypeToDelete(null);
  };

  const closeEditDialog = () => {
    setShowEditDialog(false);
    setEditingLeaveType(null);
    setNewLeaveType({ id: "", name: "", maxDays: "" });
  };

  return (
    <div className="mlt-container">
      <div className="mlt-content">
        {/* Toast notification */}
        <Toast 
          type={notification.type} 
          message={notification.message} 
          onClose={() => setNotification({ type: '', message: '' })} 
        />

        {/* Delete confirmation dialog */}
        {showDeleteConfirm && (
          <div className="mlt-dialog-overlay">
            <div className="mlt-dialog mlt-delete-dialog">
              <div className="mlt-dialog-header">
                <h2>Confirm Delete</h2>
                <button className="mlt-dialog-close-btn" onClick={cancelDeleteLeaveType}>
                  <X size={20} />
                </button>
              </div>
              <div className="mlt-dialog-content">
                <p>Are you sure you want to delete this leave type?</p>
                <p className="mlt-warning-text">This action cannot be undone.</p>
              </div>
              <div className="mlt-dialog-buttons">
                <button
                  type="button"
                  className="mlt-cancel-btn"
                  onClick={cancelDeleteLeaveType}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="mlt-delete-btn"
                  onClick={confirmDeleteLeaveType}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mlt-header">
          <div className="mlt-header-icon">
            <Calendar size={28} color="#f3f4f6" />
          </div>
          <div className="mlt-header-text">
            <h1>Manage Leave Types</h1>
            {companyName && (
              <p className="mlt-company-name">{companyName}</p>
            )}
          </div>
        </div>

        {/* Notification Messages */}
        {error && (
          <div className="mlt-notification mlt-error">
            <AlertCircle size={20} />
            <span>{error}</span>
            <button onClick={() => setError("")} className="mlt-close-btn">
              <X size={16} />
            </button>
          </div>
        )}
        
        {success && (
          <div className="mlt-notification mlt-success">
            <Check size={20} />
            <span>{success}</span>
            <button onClick={() => setSuccess("")} className="mlt-close-btn">
              <X size={16} />
            </button>
          </div>
        )}

        <div className="mlt-grid">
          {/* Add Leave Type Form */}
          <div className="mlt-card mlt-form-card">
            <div className="mlt-card-header">
              <h2>Add New Leave Type</h2>
            </div>
            <form onSubmit={handleAddLeaveType} className="mlt-form">
              <div className="mlt-form-group">
                <label htmlFor="leaveId">Leave ID</label>
                <input
                  id="leaveId"
                  type="text"
                  placeholder="Enter unique ID"
                  value={newLeaveType.id}
                  onChange={(e) => setNewLeaveType({ ...newLeaveType, id: e.target.value })}
                  required
                />
              </div>
              
              <div className="mlt-form-group">
                <label htmlFor="leaveName">Leave Name</label>
                <input
                  id="leaveName"
                  type="text"
                  placeholder="Enter leave type name"
                  value={newLeaveType.name}
                  onChange={(e) => setNewLeaveType({ ...newLeaveType, name: e.target.value })}
                  required
                />
              </div>
              
              <div className="mlt-form-group">
                <label htmlFor="maxDays">Maximum Days</label>
                <input
                  id="maxDays"
                  type="number"
                  min="1"
                  placeholder="Enter maximum allowed days"
                  value={newLeaveType.maxDays}
                  onChange={(e) => setNewLeaveType({ ...newLeaveType, maxDays: e.target.value })}
                  required
                />
              </div>
              
              <button type="submit" className="mlt-submit-btn" disabled={loading}>
                {loading ? (
                  <>
                    <Loader size={16} className="mlt-spinner" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Add Leave Type
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Existing Leave Types */}
          <div className="mlt-card mlt-list-card">
            <div className="mlt-card-header">
              <h2>Existing Leave Types</h2>
            </div>
            
            {loading ? (
              <div className="mlt-loading">
                <Loader size={40} className="mlt-spinner" />
                <p>Loading leave types...</p>
              </div>
            ) : Array.isArray(leaveTypes) && leaveTypes.length > 0 ? (
              <div className="mlt-table-container">
                <table className="mlt-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Max Days</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveTypes.map((type) => (
                      <tr key={type.id} className="mlt-table-row">
                        <td>{type.id}</td>
                        <td>{type.name}</td>
                        <td>
                          <span className="mlt-days-badge">
                            {type.maxDays} days
                          </span>
                        </td>
                        <td className="mlt-actions-cell">
                          <div className="mlt-actions-buttons">
                            <button 
                              className="mlt-action-btn mlt-edit-btn" 
                              onClick={() => handleEditLeaveType(type)}
                              title="Edit Leave Type"
                            >
                              <Edit size={16} />
                            </button>
                            <button 
                              className="mlt-action-btn mlt-deletez-btn" 
                              onClick={() => handleDeleteLeaveType(type.id, type.documentId)}
                              title="Delete Leave Type"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mlt-empty-state">
                <Calendar size={48} color="#6b7280" />
                <h3>No Leave Types</h3>
                <p>No leave types available for this company.</p>
              </div>
            )}
          </div>
        </div>

        {/* Edit Leave Type Dialog */}
        {showEditDialog && (
          <div className="mlt-dialog-overlay">
            <div className="mlt-dialog">
              <div className="mlt-dialog-header">
                <h2>Edit Leave Type</h2>
                <button className="mlt-dialog-close-btn" onClick={closeEditDialog}>
                  <X size={20} />
                </button>
              </div>
              
              <form onSubmit={handleUpdateLeaveType} className="mlt-form">
                <div className="mlt-form-group">
                  <label htmlFor="editLeaveId">Leave ID</label>
                  <input
                    id="editLeaveId"
                    type="text"
                    placeholder="Enter unique ID"
                    value={newLeaveType.id}
                    onChange={(e) => setNewLeaveType({ ...newLeaveType, id: e.target.value })}
                    required
                    disabled
                  />
                </div>
                
                <div className="mlt-form-group">
                  <label htmlFor="editLeaveName">Leave Name</label>
                  <input
                    id="editLeaveName"
                    type="text"
                    placeholder="Enter leave type name"
                    value={newLeaveType.name}
                    onChange={(e) => setNewLeaveType({ ...newLeaveType, name: e.target.value })}
                    required
                  />
                </div>
                
                <div className="mlt-form-group">
                  <label htmlFor="editMaxDays">Maximum Days</label>
                  <input
                    id="editMaxDays"
                    type="number"
                    min="1"
                    placeholder="Enter maximum allowed days"
                    value={newLeaveType.maxDays}
                    onChange={(e) => setNewLeaveType({ ...newLeaveType, maxDays: e.target.value })}
                    required
                  />
                </div>
                
                <div className="mlt-dialog-buttons">
                  <button type="button" className="mlt-cancel-btn" onClick={closeEditDialog}>
                    Cancel
                  </button>
                  <button type="submit" className="mlt-update-btn" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader size={16} className="mlt-spinner" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Check size={16} />
                        Update Leave Type
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageLeaveTypes;
